import closePopupOperation from 'editor/src/store/app/module/popup/operation/closePopupOperation';
import confirmContentPagesRemovalOperation from 'editor/src/store/design/operation/spreadManipulation/confirmContentPagesRemovalOperation';
import { isAllowedPageCount, mergePageCountLimits } from 'editor/src/store/design/util/pageCountControls';
import { pageCountDropLoss } from 'editor/src/store/design/util/pageCountSwitchLoss';
import type { Thunk } from 'editor/src/store/hooks';
import isPageCountOptionsEnabled from 'editor/src/store/hostSettings/selector/isPageCountOptionsEnabled';
import getAllMatchingVariations from 'editor/src/store/variants/helpers/getAllMatchingVariations';
import { UNSELECTED_PAGE_COUNT } from 'editor/src/store/variants/reducer/setPageCountReducer';
import getECommercePageCounts from 'editor/src/store/variants/selector/getECommercePageCounts';

import sendPostMessage from 'editor/src/util/postMessages/sendPostMessage';

import checkForMissingTemplatesOperation from './checkForMissingTemplatesOperation';
import resetPageCountOperation from './resetPageCountOperation';
import selectPageCountOperation from './selectPageCountOperation';

// Every request takes a ticket, so the dialog of one a newer request replaced can no longer move the
// design or answer: the host keeps only its latest choice and reads any answer as the one to it.
let latestRequest = 0;
let openDialog: unknown;

// The host keeps the page counts it sells in step with the design, so it waits for an answer to
// every request: a refused or cancelled move answers with the page count the design stays on.
const hostSelectPageCountOperation =
  (pageCount: number): Thunk =>
  (dispatch, getState) => {
    if (!isPageCountOptionsEnabled(getState())) {
      return;
    }

    latestRequest += 1;
    const request = latestRequest;
    if (openDialog && getState().app.popup.data === openDialog) {
      dispatch(closePopupOperation());
    }
    openDialog = undefined;

    const replyCurrentPageCount = () => {
      const current = getState().design.designData?.page_count;
      if (current) {
        sendPostMessage('design.pageCountChanged', { pageCount: current });
      }
    };

    const { designData } = getState().design;
    const { selectedPageCount } = getState().variants;
    // A move still waiting for its design gives way to this request, even one for the same page
    // count, which asks the host again: expecting the shown page count makes the design loader
    // refuse the late design, and the answer to this request is then the only one the host gets.
    if (
      designData?.page_count &&
      selectedPageCount !== UNSELECTED_PAGE_COUNT &&
      selectedPageCount !== designData.page_count
    ) {
      dispatch(selectPageCountOperation(designData.page_count));
    }

    const state = getState();
    // The same window the page controls gate on, so the host cannot put the design on a page count
    // the merchant could not pick: off the template's grid, or not sold for the selected variant.
    const limit = mergePageCountLimits(designData?.page_count_limit, getECommercePageCounts(state));
    const isAllowed = isAllowedPageCount(pageCount, limit);
    if (!designData?.page_count || !isAllowed || pageCount === designData.page_count) {
      replyCurrentPageCount();
      return;
    }

    // Unlike the Product tab control, this also moves a book with several formats selected: each
    // gets its template at the new page count, as after adding or removing a spread.
    const move = () => {
      const latestDesign = getState().design.designData;
      dispatch(resetPageCountOperation(pageCount, latestDesign?.dimensions, latestDesign?.product_uid || ''));

      const { product, configuration, selectedMultiOptions, selectedSingleOptions, replaceControlKeys } =
        getState().variants;
      if (!configuration.singleSelection) {
        dispatch(
          checkForMissingTemplatesOperation(
            getAllMatchingVariations(
              product.productVariations,
              selectedMultiOptions,
              selectedSingleOptions,
              false,
              replaceControlKeys,
            ),
          ),
        );
      }
    };

    const { pages, holdsContent } = pageCountDropLoss(state, pageCount);
    if (holdsContent) {
      const unlessReplaced = (answer: () => void) => () => {
        if (request === latestRequest) {
          openDialog = undefined;
          answer();
        }
      };
      dispatch(confirmContentPagesRemovalOperation(pages, unlessReplaced(move), unlessReplaced(replyCurrentPageCount)));
      openDialog = getState().app.popup.data;
      return;
    }
    move();
  };

export default hostSelectPageCountOperation;
